import React, { useCallback, useEffect, useRef, useState } from 'react';
import styles from './cwv-analyser.module.css';

export interface CwvIssueField {
  text: string;
  code: string;
}

export interface CwvIssue {
  id: string;
  severity: 'error' | 'warning' | 'info';
  category: string;
  metric: 'LCP' | 'INP' | 'CLS' | 'General';
  message: CwvIssueField;
  fix: CwvIssueField;
}

type Status = 'idle' | 'loading' | 'done' | 'error' | 'not_configured' | 'rate_limited' | 'code_too_large';

interface Props {
  code: string;
  context: 'head' | 'body';
  pageTab: string;
  publisherId: string;
  runRef?: React.MutableRefObject<{ run: () => void; stop: () => void } | null>;
  onStatusChange?: (isLoading: boolean) => void;
  onWidthChange?: (width: number) => void;
  onIssueCodeClick?: (code: string) => void;
}

const SEVERITY_LABEL: Record<CwvIssue['severity'], string> = {
  error: 'Critical',
  warning: 'Warning',
  info: 'Info'
};

const METRIC_COLOUR: Record<CwvIssue['metric'], string> = {
  LCP: styles.metricLcp,
  INP: styles.metricInp,
  CLS: styles.metricCls,
  General: styles.metricGeneral
};

function errorCodeToStatus(code: string): Status {
  if (code === 'analyser_not_configured') return 'not_configured';
  if (code === 'rate_limit_exceeded') return 'rate_limited';
  if (code === 'code_too_large') return 'code_too_large';
  return 'error';
}

const MIN_PANEL_WIDTH = 280;
const MAX_PANEL_WIDTH = 480;

const CwvAnalyserPanel = ({ code, context, pageTab, publisherId, runRef, onStatusChange, onWidthChange, onIssueCodeClick }: Props) => {
  const [issues, setIssues] = useState<CwvIssue[]>([]);
  const [status, setStatus] = useState<Status>('idle');
  const [analysedFor, setAnalysedFor] = useState<{ pageTab: string; context: 'head' | 'body' } | null>(null);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const abortRef = useRef<AbortController | null>(null);
  const panelRef = useRef<HTMLElement | null>(null);
  const isDragging = useRef(false);
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(0);

  const onResizeMouseDown = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    isDragging.current = true;
    dragStartX.current = e.clientX;
    dragStartWidth.current = panelRef.current?.getBoundingClientRect().width ?? MIN_PANEL_WIDTH;

    const panelRightEdge = panelRef.current
      ? panelRef.current.getBoundingClientRect().right
      : window.innerWidth;

    const onMouseMove = (ev: MouseEvent) => {
      if (!isDragging.current) return;
      const delta = dragStartX.current - ev.clientX;
      const viewportMax = panelRightEdge - 8;
      const newWidth = Math.min(MAX_PANEL_WIDTH, viewportMax, Math.max(MIN_PANEL_WIDTH, dragStartWidth.current + delta));
      onWidthChange?.(newWidth);
    };

    const onMouseUp = () => {
      isDragging.current = false;
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };

    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  }, []);

  const runAnalysis = useCallback(async () => {
    if (!code.trim() || status === 'loading') return;

    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();

    setAnalysedFor({ pageTab, context });
    setStatus('loading');
    setIssues([]);
    onStatusChange?.(true);

    try {
      const res = await fetch(`/api/v1/accounts/${publisherId}/cwv-analyse`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ code, context, pageTab }),
        signal: abortRef.current.signal
      });

      const data = await res.json();

      if (data.error) {
        setStatus(errorCodeToStatus(data.error));
        onStatusChange?.(false);
        return;
      }

      setIssues(data.recommendations || []);
      setStatus('done');
      onStatusChange?.(false);
    } catch (err) {
      if (err && (err as any).name === 'AbortError') return;
      setStatus('error');
      onStatusChange?.(false);
    }
  }, [code, context, pageTab, publisherId, status, onStatusChange]);

  const stopAnalysis = useCallback(() => {
    if (abortRef.current) abortRef.current.abort();
    setStatus('idle');
    setAnalysedFor(null);
  }, []);

  useEffect(() => {
    if (runRef) runRef.current = { run: runAnalysis, stop: stopAnalysis };
  }, [runAnalysis, stopAnalysis, runRef]);

  const errorCount = issues.filter(i => i.severity === 'error').length;
  const warnCount = issues.filter(i => i.severity === 'warning').length;
  const infoCount = issues.filter(i => i.severity === 'info').length;

  useEffect(() => {
    const el = panelRef.current;
    if (!el) return;
    const onScroll = () => setShowScrollTop(el.scrollTop > 150);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, []);

  const scrollToTop = () => {
    if (panelRef.current) panelRef.current.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const liveLabel =
    status === 'loading' ? 'Analysing your code, please wait' :
      status === 'done' ? (issues.length > 0 ? `${issues.length} CWV issue${issues.length > 1 ? 's' : ''} found` : 'No CWV issues detected') :
        status === 'error' ? 'Analysis unavailable' :
          status === 'code_too_large' ? 'Code exceeds the maximum size limit' :
            'CWV analyser ready';

  return (
    <section
      ref={panelRef}
      className={styles.panel}
      aria-label="CWV Impact Analysis"
      aria-busy={status === 'loading'}
    >
      <div
        className={styles.resizeHandle}
        onMouseDown={onResizeMouseDown}
        aria-hidden="true"
        title="Drag to resize panel"
      />
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleRow}>
          <h2 className={styles.panelTitle}>CWV Impact Analysis</h2>
          {status === 'done' && (
            <span
              className={issues.length > 0 ? styles.statusBadgeError : styles.statusBadgeClean}
              aria-label={issues.length > 0 ? `${issues.length} issues found` : 'No issues detected'}
            >
              {issues.length > 0
                ? `${issues.length} issue${issues.length > 1 ? 's' : ''} found`
                : 'No issues'}
            </span>
          )}
        </div>
        {analysedFor && (status === 'loading' || status === 'done') && (
          <p className={styles.panelSubtitle} aria-live="polite">
            {status === 'loading' ? 'Analysing' : 'Results for'}{' '}
            <strong>{analysedFor.pageTab}</strong>
            {' — '}
            <code className={styles.panelSubtitleCode}>&lt;{analysedFor.context}&gt;</code>
          </p>
        )}
      </div>

      <div
        aria-live="polite"
        aria-atomic="true"
        aria-label={liveLabel}
        className={styles.liveRegion}
      >
        {status === 'idle' && (
          <p className={styles.idleHint}>
            Click <strong>Run Analyser</strong> to check this code for CWV issues.
          </p>
        )}

        {status === 'loading' && (
          <div className={styles.loadingWrapper} role="status">
            <span className={styles.spinner} aria-hidden="true" />
            <span className={styles.loadingText}>Analysing your code…</span>
          </div>
        )}

        {status === 'error' && (
          <p className={styles.errorHint} role="alert">
            ⚠ Analysis unavailable. Check your connection and try again.
          </p>
        )}

        {status === 'not_configured' && (
          <p className={styles.errorHint} role="alert">
            ⚠ Analyser not configured. Add Azure OpenAI credentials to page-builder.yml.
          </p>
        )}

        {status === 'code_too_large' && (
          <p className={styles.errorHint} role="alert">
            ⚠ Your code exceeds the 300 KB size limit. Reduce the amount of code and try again.
          </p>
        )}

        {status === 'rate_limited' && (
          <p className={styles.errorHint} role="alert">
            ⚠ Too many requests. Please wait a moment before running the analyser again.
          </p>
        )}

        {status === 'done' && issues.length === 0 && (
          <p className={styles.cleanHint} role="status">
            ✅ No CWV issues detected. Your code looks clean.
          </p>
        )}

        {status === 'done' && issues.length > 0 && (
          <>
            <div className={styles.summaryStrip} role="summary" aria-label="Issue summary">
              <div className={styles.summaryRow}>
                {errorCount > 0 && (
                  <span className={styles.summaryError} aria-label={`${errorCount} critical issues`}>
                    ● {errorCount} critical
                  </span>
                )}
                {warnCount > 0 && (
                  <span className={styles.summaryWarn} aria-label={`${warnCount} warnings`}>
                    ⚠ {warnCount} warning{warnCount > 1 ? 's' : ''}
                  </span>
                )}
                {infoCount > 0 && (
                  <span className={styles.summaryInfo} aria-label={`${infoCount} informational suggestions`}>
                    ℹ {infoCount} info
                  </span>
                )}
              </div>
              <div className={styles.summaryImpact} aria-hidden="true">
                ⚠ Your CWV will be affected
              </div>
            </div>

            <ul
              className={styles.issueList}
              aria-label={`${issues.length} Core Web Vitals issue${issues.length > 1 ? 's' : ''} detected`}
            >
              {issues.map(issue => (
                <li key={issue.id} className={styles.issueItem}>
                  <span
                    className={`${styles.severityDot} ${styles[`dot_${issue.severity}`]}`}
                    aria-hidden="true"
                  />
                  <div className={styles.issueContent}>
                    <p className={styles.issueTitle}><b>Issue: </b><br />{issue.message.text}</p>
                    {issue.message.code && (
                      <div className={styles.issueCodeWrapper}>
                        <pre
                          className={`${styles.issueCode} ${onIssueCodeClick ? styles.issueCodeClickable : ''}`}
                          title={onIssueCodeClick ? 'Click to locate in editor' : undefined}
                          onClick={() => onIssueCodeClick?.(issue.message.code)}
                          role={onIssueCodeClick ? 'button' : undefined}
                          tabIndex={onIssueCodeClick ? 0 : undefined}
                          onKeyDown={e => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              e.stopPropagation();
                              if (onIssueCodeClick) onIssueCodeClick(issue.message.code);
                            }
                          }}
                          aria-label={onIssueCodeClick ? 'Click to locate this code in the editor' : undefined}
                        ><code>{issue.message.code}</code></pre>
                        {onIssueCodeClick && (
                          <span className={styles.codeLocateHint} aria-hidden="true">↖ Double click to locate</span>
                        )}
                        <button
                          className={styles.codeCopyBtn}
                          title="Copy to clipboard"
                          aria-label="Copy code"
                          onClick={() => navigator.clipboard.writeText(issue.message.code)}
                        >
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                            <rect x="9" y="9" width="13" height="13" rx="2" ry="2" />
                            <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1" />
                          </svg>
                        </button>
                      </div>
                    )}
                    <p className={styles.issueFix}>🤖<b>AI Recommendation:</b> <br />{issue.fix.text}</p>
                    {issue.fix.code && (
                      <pre className={styles.issueCode}><code>{issue.fix.code}</code></pre>
                    )}
                    <div className={styles.issueMeta} aria-label={`Affects ${issue.metric}, severity: ${SEVERITY_LABEL[issue.severity]}`}>
                      <span className={`${styles.metricPill} ${METRIC_COLOUR[issue.metric]}`} aria-hidden="true">
                        ↗ {issue.metric}
                      </span>
                      <span className={`${styles.categoryPill} ${styles[`sev_${issue.severity}`]}`} aria-hidden="true">
                        {SEVERITY_LABEL[issue.severity]}
                      </span>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          </>
        )}

      </div>
    </section>
  );
};

export default CwvAnalyserPanel;
